import { ExclamationCircleOutlined, WarningOutlined } from "@ant-design/icons";
import {
	Button,
	Form,
	Input,
	Modal,
	Popconfirm,
	Popover,
	Radio,
	Select,
	Space,
	Tabs,
	Tag,
} from "antd";
import { useForm } from "antd/es/form/Form";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import "../assets/scss/projects.scss";
import FormButtonCancel from "../components/FormButtonCancel";
import FormButtonSave from "../components/FormButtonSave";
import LookupMissingTooltip from "../components/LookupMissingTooltip";
import usePermission from "../hooks/usePermission";
import { usePermissionStore } from "../stores/permissionStore";
import { getBusinessUnitList } from "../services/api-server/business_unit";
import { getCompanyList } from "../services/api-server/company";
import { getRolePermissionList } from "../services/api-server/general_roles";
import { getTenantSpecificUsers } from "../services/api-server/graphql";
import { getMultiAssetProgrammeTemplate } from "../services/api-server/multi_asset_programme_template";
import { getOperatingOfficeList } from "../services/api-server/operating_office";
import { getOperationTypeList } from "../services/api-server/operation_type";
import { getProjectTagList } from "../services/api-server/project_tags";
import {
	addProjectItem,
	getProjects,
	updateProjectItem,
} from "../services/api-server/projects";
import { getRegionList } from "../services/api-server/region";
import { useProjectStore } from "../stores/projectStore";
import { alertMessage } from "../utils/alert";
import { GetAntIcon } from "../utils/ant_icons";
import { countryList } from "../utils/countryCodes";
import Emitter from "../utils/emitter";
import { projectArea, projectType } from "../utils/test";
import { HomeContext } from "./Home";
import NoAccess from "./NoAccess";
import ProjectOutline from "./projectTabs/ProjectOutline";
import ProjectTeam from "./projectTabs/ProjectTeam";
import { getUserRole } from "../services/api-server/roles_permissions";
// import ProjectDetails from "../projectTabs/ProjectDetails";

const { Option } = Select;

/*
	- currentProject is the project code of the current project
*/
function Projects(props: any) {
	const context: any = useContext(HomeContext);
	const location = useLocation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [allData, setAllData] = useState<any>({});

	const { setProject } = useProjectStore();

	const [allProjects, setAllProjects] = useState<any>([]);
	const [currentProject, setCurrentProject] = useState<any>(null);
	const [currentProjectData, setCurrentProjectData] = useState<any>(null);
	const [formRef]: any = useForm();
	const [formReset, setFormReset] = useState<boolean>(true);

	// Watch the project_scope field to conditionally show project template
	const projectScopeValue = Form.useWatch("project_scope", formRef);

	// Track the initial project template value to determine if it was pre-populated
	const [initialProjectTemplate, setInitialProjectTemplate] =
		useState<any>(null);
	const [businessUnits, setBusinessUnits] = useState<any>(null);
	const [projectTags, setProjectTags] = useState<any>(null);
	const [operatingOffices, setOperatingOffices] = useState<any>(null);
	const [companies, setCompanies] = useState<any>(null);
	const [regions, setRegions] = useState<any>(null);
	const [projectTemplates, setProjectTemplates] = useState<any>([]);
	const [countries, setCountries] = useState<any>(null);
	const [roles, setRoles] = useState<any>(null);
	const [users, setUsers] = useState<any>(null);
	const [emptyModalOpen, setEmptyModalOpen] = useState<any>(null);

	const [initialForm, setInitialForm] = useState<any>(null);
	const [dropdownOpen, setDropdownOpen] = useState<any>(false);
	const [switchSelected, setSwitchSelected] = useState<any>(false);

	const [history, setHistory] = useState<any>([]);
	const [sideContainerOpen, setSideContainerOpen] = useState<any>(false);
	// const [teamUpdated, setTeamUpdated] = useState<any>(false);

	const [readOnly, setReadOnly] = useState<boolean>(false);

	const navTabKey = location?.state?.tabKey;
	const navTabPayload = location?.state?.phaseRecord;

	const hasCreateProjectPermission = usePermission("Create.Project");
	const isExaminerOnly = usePermissionStore(
		(state) => !!state.accessProfile?.restricted
	);
	const hasEditProjectPermission = usePermission(
		"Edit.Project",
		currentProject
	);
	const hasDeleteProjectPermission = usePermission(
		"Delete.Project",
		currentProject
	);

	const projectScopeOptions = [
		{ label: "Single Asset", value: "single-asset" },
		{ label: "Multi Asset", value: "multi-asset" },
	];

	const setTabPopOverContent = (type: any) => {
		if (currentProject == "Add new project") {
			return "Will be enabled after updating 'Project details'.";
		} else if (
			type === "project-outline" &&
			!(
				context?.currentProject?.project_type === "Project Management" &&
				context?.currentProject?.project_area === "Wells"
			)
		) {
			return "Applicable only to 'Project Management' type with 'Wells' as the project area.";
		} else if (context?.currentProject?.project_type === "Consulting") {
			return "Not applicable to 'Consulting' project type.";
		} else if (
			type === "project-outline" &&
			!currentProjectData?.project_manager
		) {
			return "Will be enabled after updating 'Project team'.";
		} else {
			return "";
		}
	};

	const TabList: any = [
		{ label: "Project details", key: "project-details" },
		{
			label: (
				<Popover content={setTabPopOverContent("project-team")}>
					<span>Project team</span>
				</Popover>
			),
			key: "project-team",
			disabled: currentProject == "Add new project",
		},
		{
			label: (
				<Popover content={setTabPopOverContent("project-outline")}>
					<span>Project outline</span>
				</Popover>
			),
			key: "project-outline",
			disabled:
				currentProject == "Add new project" ||
				!currentProjectData?.project_manager ||
				!(
					(context?.currentProject?.project_type === "Project Management" ||
						context?.currentProject?.project_scope === "multi-asset") &&
					context?.currentProject?.project_area === "Wells"
				)
					? true
					: false,
		},
	];

	const [tabKey, setTabKey] = useState<any>(() => {
		if (navTabKey) {
			return navTabKey;
		} else if (sessionStorage.getItem("main-tabkey")) {
			let sessionTabKey: any = sessionStorage.getItem("main-tabkey");
			let found = TabList.find((tab: any) => {
				return tab.key === sessionTabKey;
			});
			if (found) {
				return sessionTabKey;
			} else {
				sessionStorage.setItem("main-tabkey", TabList[0].key);
				return TabList[0].key;
			}
		} else {
			sessionStorage.setItem("main-tabkey", TabList[0].key);
			return TabList[0].key;
		}
	});

	const [isLoading, setIsLoding] = useState<any>(true);

	// Load all the data needed for the form
	useEffect(() => {
		if ((context.currentTenantKey, context.allData)) {
			Emitter.emit("loading", {
				name: "project-settings",
				isLoading: true,
			});

			let allData: any = {};
			Promise.all([
				getBusinessUnitList(context.currentTenantKey).then((_data: any) => {
					if (_data) {
						let data = _data.map((item: any) => {
							item.value = item.uuid;
							item.label = item.name;
							return item;
						});
						data = sortData(data);
						setBusinessUnits(data);
						allData.business_units = data;
					}
				}),

				getProjectTagList(context.currentTenantKey).then((_data: any) => {
					if (_data) {
						let data = _data.map((item: any) => {
							item.color = item.color;
							item.value = item.uuid;
							item.label = item.name;
							return item;
						});
						data = sortData(data);
						allData.project_tags = data;
						setProjectTags(data);
					}
				}),

				getOperatingOfficeList(context.currentTenantKey).then((_data: any) => {
					if (_data) {
						let data = _data.map((item: any) => {
							item.value = item.uuid;
							item.label = `${item.name}, ${item.country}`;
							return item;
						});
						data = sortData(data);
						allData.operating_offices = data;
						setOperatingOffices(data);
					}
				}),

				getMultiAssetProgrammeTemplate(context.currentTenantKey).then(
					(_data: any) => {
						if (_data) {
							let data = _data.map((item: any) => {
								item.value = item._id;
								item.label = item.name;
								return item;
							});
							data = sortData(data);
							setProjectTemplates(data);
						}
					}
				),

				getOperationTypeList().then((_data: any) => {
					if (_data) {
						let data = _data.map((item: any) => {
							item.value = item.uuid;
							item.label = item.name;
							return item;
						});
						data = sortData(data);
						allData.operation_types = data;
					}
				}),

				getProjects(context.currentTenantKey).then((_data: any) => {
					if (_data) {
						let data = _data
							.filter((item: any) => !item.is_removed)
							.map((item: any) => {
								item.value = item.project_code;
								item.label = item.project_name;
								return item;
							});
						data = sortData(data);
						allData.projects = data;
						setAllProjects(data);
					} else {
						allData.projects = [];
						setAllProjects([]);
					}
				}),

				getRegionList(context.currentTenantKey).then((_data: any) => {
					if (_data) {
						let data = _data.map((item: any) => {
							item.value = item.uuid;
							item.label = item.name;
							return item;
						});
						data = sortData(data);
						allData.regions = data;
						setRegions(data);
					}
				}),

				// getCompanyList(context?.currentTenantData?.company_list).then(
				getCompanyList(context?.currentTenantKey, [], true, "Operator").then(
					(_data: any) => {
						if (_data) {
							let data = _data.map((item: any) => {
								item.value = item.uuid;
								item.label = item.name;
								return item;
							});
							data = sortData(data);
							allData.companies = data;
							setCompanies(data);
						}
					}
				),

				getRolePermissionList(context.currentTenantKey).then((_data: any) => {
					if (_data) {
						let data = _data.map((item: any) => {
							item.key = item.uuid;
							item.value = item.name;
							item.label = item.name;
							return item;
						});
						data = sortData(data);
						allData.roles = data;
						setRoles(data);
					}
				}),

				getTenantSpecificUsers(
					context?.currentTenantData?.uuid || context?.currentTenantData?.id
				).then((tenantUsers: any) => {
					if (tenantUsers) {
						let data = tenantUsers.map((item: any) => {
							// console.log(item);
							item.key = item.id;
							item.value = item.mail;
							item.label = item.mail;
							return item;
						});
						console.log("tenant specific users data", data);
						data = sortData(data || []);
						allData.users = data || [];

						setUsers(data);
					}
					if (context?.currentTenantData?.project_managers) {
						let projectManagerList =
							context?.currentTenantData?.project_managers;
						let data = projectManagerList.map((item: any) => {
							let obj: any = {};
							obj.key = item;
							obj.value = item;
							obj.label = item;
							return obj;
						});
						data = sortData(data);
						allData.project_managers = data;
					}
				}),
			])
				.then((data: any) => {
					setAllData(allData);
				})
				.finally(() => {
					if (location.state) {
						let foundProject = allData?.projects?.find(
							(project: any) =>
								project?.project_code === location.state.project_code
						);

						if (foundProject) {
							resetForm(foundProject, allData);
							setCurrentProject(foundProject.project_code);
							setCurrentProjectData(foundProject);
							context.setCurrentProject(foundProject);
						} else {
							setCurrentProject(null);
							setCurrentProjectData(null);
						}
					} else {
						if (
							location.pathname.split("/")[2] === "add-new-project" &&
							location.pathname.split("/").length == 3
						) {
							setCurrentProject("Add new project");
							setCurrentProjectData(null);
							resetForm(null, allData);
						} else {
							let foundProject = allData?.projects?.find(
								(project: any) =>
									project?.project_code === location.pathname.split("/")[2]
							);

							if (foundProject) {
								resetForm(foundProject, allData);
								setCurrentProject(foundProject.project_code);
								setCurrentProjectData(null);
								context.setCurrentProject(foundProject);
							} else {
								setCurrentProject(null);
								setCurrentProjectData(null);
							}
						}
					}
					setIsLoding(false);
					Emitter.emit("loading", {
						name: "project-settings",
						isLoading: false,
					});
				});
		}
	}, [context.currentTenantKey, context.allData, location.pathname]);

	// Set the tab to 'project-details' when the currentProject is "Add new project"
	useEffect(() => {
		if (currentProject) {
			// Initialize the currentProject in local storage
			localStorage.setItem("currentProject", currentProject);
			if (currentProject === "Add new project") {
				setTabKey("project-details");
			}
		}
	}, [currentProject]);

	useEffect(() => {
		if (!hasEditProjectPermission.hasPermission) {
			if (currentProject && currentProject !== "Add new project") {
				setReadOnly(true);
			}
		} else {
			setReadOnly(false);
		}
	}, [hasEditProjectPermission, currentProject]);

	// check lookup values for?
	const checkLookup = (key: any, value: any, data: any) => {
		switch (key) {
			case "business_unit":
				return (
					data["business_units"]?.find((element: any) => {
						return element.uuid == value;
					})?.uuid || null
				);
			case "client":
				return (
					data["companies"]?.find((element: any) => {
						return element.uuid == value;
					})?.uuid || null
				);
			case "region":
				return (
					data["regions"]?.find((element: any) => {
						return element.uuid == value;
					})?.uuid || null
				);
			case "country":
				return (
					countryList?.find((element: any) => {
						return element["alpha-3"] == value;
					})["alpha-3"] || null
				);
			case "operating_office":
				return (
					data["operating_offices"]?.find((element: any) => {
						return element.uuid == value;
					})?.uuid || null
				);
			case "project_tags":
				return value?.map((tag: any) => {
					return (
						data["project_tags"]?.find((element: any) => {
							return element.uuid == tag;
						})?.uuid || "Lookup missing"
					);
				});

			default:
				return value;
		}
	};

	// reset form
	const resetForm = (values: any = null, data: any = null) => {
		let filteredCountries: any = [];
		if (formRef) {
			setFormReset(true);
			if (values) {
				Object.keys(values).forEach((key: any) => {
					values[key] = checkLookup(key, values[key], data || allData);
					// console.log(key, values[key]);
				});
			}
			formRef.resetFields();

			formRef.setFieldsValue(values);

			// Capture the initial project template value to determine if it was pre-populated
			setInitialProjectTemplate(values?.project_template || null);

			if (formRef.getFieldValue("region") || values?.region) {
				let regionList = data?.regions || regions;
				let indexArray = regionList?.find(
					(object: any) => object.uuid === formRef.getFieldValue("region")
				);
				if (indexArray) {
					let filteredData: any = [];
					filteredData = indexArray?.countries
						?.map((object: any) => {
							let countries = countryList?.find(
								(element: any) => element["alpha-3"] == object
							);
							if (countries)
								return {
									label: countries.name,
									value: countries["alpha-3"],
								};
						})
						.filter((item: any) => item);

					filteredCountries = filteredData.length > 0 ? filteredData : null;

					setCountries(filteredCountries);
				} else {
					setCountries(filteredCountries);
				}
			} else {
				setCountries(null);
			}
		}
	};

	// Function to sort array of objects alphabetically based on property's name
	const sortData = (data: any) => {
		data.sort((a: any, b: any) => {
			const labelA = a.label?.toLowerCase() || "";
			const labelB = b.label?.toLowerCase() || "";

			if (labelA < labelB) {
				return -1;
			}
			if (labelA > labelB) {
				return 1;
			}
			return 0;
		});
		return data;
	};

	// Check if lookup value exist in the dropdown options
	const renderLookupMissing = (key: any) => {
		let value = formRef.getFieldValue(key);
		if (value === null && value != "") {
			return (
				<LookupMissingTooltip
					userRole={props.params.userRole}
					content={
						<div
							style={{
								position: "absolute",
								padding: "0 11px",
								color: "#FAAD14",

								top: "5px",
								display: "flex",
								gap: "8px",
							}}
						>
							<WarningOutlined />
							Lookup missing
						</div>
					}
				/>
			);
		}
	};

	// Handle when project has change / adding a new project
	const handleProjectChange = async (
		key: any = null,
		data: any = null,
		manual: boolean = false
	) => {
		try {
			let projects = data?.projects || allProjects;
			await context.handlePageChange();
			// Emitter.emit("loading", {name: "project-settings",isLoading: true,});
			setCurrentProject(key);

			//Always set tabKey to project-details when project has changed
			if (manual) {
				setTabKey("project-details");
				sessionStorage.removeItem("tabKey");
			}

			if (key == "Add new project" || key == null) {
				Emitter.emit("projectName", "Add new project");
				setTabKey("project-details");
				sessionStorage.removeItem("tabKey");
				resetForm(null);
			} else {
				sessionStorage.removeItem("tabKey");

				const project = projects.find((project: any) => {
					// Emit the project name to set the breadcrumb
					Emitter.emit("projectName", project.project_name);
					return project.project_code == key;
				});
				resetForm(project, data);
				setCurrentProjectData(project);
				navigate(`/projects/${key}/project-settings`, {
					state: project,
				});
			}
			// Emitter.emit("loading", {name: "project-settings",isLoading: false,});
		} catch {}
	};

	const handleTabChange = async (key: any) => {
		try {
			await context.handlePageChange();
			setTabKey(key);
			setFormReset(true);
			sessionStorage.setItem("main-tabkey", key);
			const project = allProjects.find((project: any) => {
				return project.project_code == currentProject;
			});
			resetForm(project, null);
			setSideContainerOpen(false);
		} catch {}
	};

	// Deprecated
	const checkProjectManager = (email: any) => {
		const isTenantMember = context.currentTenantData?.tenant_members?.some(
			(mail: any) => mail.toLowerCase() === email.toLowerCase()
		);
		const isTenantAdmin = context.currentTenantData?.tenant_admin?.some(
			(mail: any) => mail.toLowerCase() === email.toLowerCase()
		);
		if (!isTenantMember && !isTenantAdmin) {
			return [email];
		} else {
			return null;
		}
	};

	// Handle antd Select value change
	const handleSelectChange = (key: any, value: any) => {
		setHistory([...history, { key: key, value: value }]);
		formRef.setFieldValue(key, value);
	};

	// Handle form save
	const handleFormSave = (formValues: any | null = null, type: any = null) => {
		let values = formValues || formRef.getFieldsValue();

		// For each values in the form, trim the leading and trailing spaces
		Object.keys(values).forEach((key) => {
			if (typeof values[key] == "string") {
				values[key] = values[key].trim();
			}
		});

		const foundCountry = countryList.find(
			(element: any) => element["alpha-3"] == values.country
		);
		if (foundCountry) {
			values.latlong = [foundCountry.latitude, foundCountry.longitude];
		} else {
			values.latlong = [0, 0];
		}

		if (currentProject != "Add new project" && currentProject) {
			values.modified_on = Date.now();

			Emitter.emit("loading", { name: "project-settings", isLoading: true });

			updateProjectItem(
				context.currentTenantKey,
				currentProject,
				values,
				initialForm,
				props?.params?.user,
				[
					"project_name",
					"project_area",
					"project_type",
					"client",
					"region",
					"country",
					"project_tags",
					"operating_office",
				]
			)
				.then((updatedData: any) => {
					alertMessage("success", "Project has been successfully updated.");
					resetForm(updatedData);
					setProject(updatedData);
					context.setCurrentProject(updatedData);
					setCurrentProjectData(updatedData);
					navigate(`/projects/${updatedData.project_code}/project-settings`, {
						state: { ...updatedData },
					});
					getProjects(context.currentTenantKey)
						.then((_data: any) => {
							if (_data) {
								let data = _data
									.filter((item: any) => !item.is_removed)
									.map((item: any) => {
										if (!item.is_removed) {
											item.value = item.project_code;
											item.label = item.project_name;
											return item;
										}
									});
								setAllProjects(data);
								setAllData({ ...allData, projects: data });
								context.allData.projects = data;
							}
						})
						.catch((error: any) => {
							console.log(error);
						});
				})
				.catch((error: any) => {
					alertMessage("error", "Failed to update project. Please try again.");
					resetForm();
				})
				.finally(() => {
					Emitter.emit("loading", {
						name: "project-settings",
						isLoading: false,
					});
				});
		} else {
			values.is_removed = false;
			values.status = "New";
			values.created_on = new Date();
			values.modified_on = new Date();
			values.project_manager = checkProjectManager(props?.params?.user?.email);

			Emitter.emit("loading", { name: "project-settings", isLoading: true });

			addProjectItem(context.currentTenantKey, values, props?.params?.user)
				.then((data: any) => {
					alertMessage("success", "New project has been successfully added.");
					resetForm(data);
					if (context.allData?.projects?.length > 0)
						context.allData?.projects?.push(data);
					else {
						context.allData.projects = [data];
					}
					context.setCurrentProject(data);
					setCurrentProjectData(data);
					setTabKey("project-team");
					setCurrentProject(data.project_code);
					setAllProjects((prev: any) => [...prev, data]);
					setAllData((prev: any) => ({
						...prev,
						projects: [...prev.projects, data],
					}));
					navigate(`/projects/${data.project_code}/project-settings`, {
						state: { ...data },
					});
					queryClient.invalidateQueries({
						queryKey: ["projects", context.currentTenantKey],
					});
					getUserRole(
						context.currentTenantKey,
						props?.params?.user?.email
					).then((user_role_data) => context.setUserRole(user_role_data || []));
				})
				.catch((error: any) => {
					console.log("Error adding new project", error);
					if (error.code == 11000 || error.status == 409) {
						alertMessage(
							"error",
							"This project already exists, please ensure the project is unique. Changes are not saved."
						);
					} else {
						alertMessage(
							"error",
							"Failed to add new project. Please try again."
						);
					}
				})
				.finally(() => {
					Emitter.emit("loading", {
						name: "project-settings",
						isLoading: false,
					});
				});
		}
	};

	// handle project configurations save (anything that is not project details)
	const handleProjectConfigSave = () => {
		getProjects(context.currentTenantKey).then((_data: any) => {
			if (_data) {
				let data = _data
					.filter((item: any) => !item.is_removed)
					.map((item: any) => {
						if (!item.is_removed) {
							item.value = item.project_code;
							item.label = item.project_name;
							return item;
						}
					});

				let updatedProject: any = _data.find(
					(project: any) => project.project_code === currentProject
				);
				// setTeamUpdated(true);
				setAllProjects(data);
				setAllData({ ...allData, projects: data });
				setCurrentProjectData(updatedProject);
			}
		});
	};

	// Handle form delete
	const handleFormDelete = (project_code: any) => {
		Emitter.emit("loading", { name: "project-settings", isLoading: true });
		context?.handleFormUpdating(false);
		context.setCurrentProject(null);
		setCurrentProjectData(null);
		Emitter.emit("projectName", "Add new project");
		const values = initialForm;
		values.project_code = project_code;
		values.is_removed = true;
		updateProjectItem(
			context.currentTenantKey,
			project_code,
			values,
			initialForm,
			props.params?.user,
			[
				"project_name",
				"project_area",
				"project_type",
				"client",
				"region",
				"country",
				"project_tags",
				"operating_office",
			]
		)
			.then((updatedData: any) => {
				setProject(updatedData);
				alertMessage("success", "Project has been successfully deleted.");
				navigate("/projects/add-new-project");
				resetForm();
			})
			.catch(() => {
				alertMessage("error", "Failed to delete project. Please try again.");
			})
			.finally(() => {
				Emitter.emit("loading", { name: "project-settings", isLoading: false });
				navigate("/projects", { replace: true });
			});
	};

	// Handle form Cancel
	const handleFormCancel = () => {
		context?.handleFormUpdating(false);
		if (currentProject != "Add new project") {
			const project = allProjects.find((project: any) => {
				return project.project_code == currentProject;
			});
			resetForm(project);
		} else {
			resetForm();
		}
	};

	const tabContent = () => {
		switch (tabKey) {
			case "project-details":
				return (
					<div className="project-details-tab-content">
						<div className="main-container">
							<div className="generic-header">Settings</div>
							<div className="generic-content">
								<div className="project-settings-form">
									{currentProject !== "Add new project" && currentProject && (
										<span className="project-id"># {currentProject}</span>
									)}
									<Form
										layout={"vertical"}
										form={formRef}
										className="project-form"
										requiredMark="optional"
										style={{ gap: "20px" }}
										onChange={(event: any) => {
											setFormReset(false);
										}}
										onSelect={() => {
											setFormReset(false);
										}}
									>
										<Form.Item
											label="Project title"
											name="project_name"
											rules={[{ required: true, message: "" }]}
										>
											<Input
												autoComplete="off"
												count={{
													show: true,
													max: 50,
												}}
												onChange={(event: any) => {
													const value = event.target.value
														.replace(/^ /, "")
														.replace(/\s+/g, " ")
														.slice(0, 50);
													// .replace(/[^a-zA-Z0-9 ]/g, "");
													formRef.setFieldsValue({
														project_name: value,
													});
												}}
												readOnly={readOnly}
											></Input>
										</Form.Item>
										<Form.Item
											label="Project area"
											name="project_area"
											rules={[{ required: true, message: "" }]}
										>
											<Select
												getPopupContainer={(trigger: any) => trigger.parentNode}
												showSearch
												options={sortData(projectArea)}
												onChange={(value: any) => {
													handleSelectChange("project_area", value);
												}}
												disabled={readOnly}
											></Select>
										</Form.Item>
										<div
											style={{
												display: "flex",
												flexDirection: "row",
												flexWrap: "wrap",
												gap: "2%",
												rowGap: "20px",
											}}
										>
											<Form.Item
												label="Project type"
												name="project_type"
												tooltip={{
													title:
														"Select the best category that aligns with the goals and characteristics of your project.",
													icon: GetAntIcon("question3"),
													placement: "right",
													overlayStyle: { maxWidth: "400px" },
												}}
												style={{ flex: 1, flexGrow: 1 }}
												rules={[{ required: true, message: "" }]}
											>
												<Select
													disabled={!(currentProject === "Add new project")}
													getPopupContainer={(trigger: any) =>
														trigger.parentNode
													}
													showSearch
													options={sortData(projectType)}
													onChange={(value: any) => {
														handleSelectChange("project_type", value);
													}}
												></Select>
												{renderLookupMissing("project_type")}
											</Form.Item>
											<Form.Item
												label="Project scope"
												name="project_scope"
												style={{ flex: 1, flexGrow: 1 }}
												rules={[{ required: true, message: "" }]}
											>
												<Radio.Group
													className="custom-radio-group"
													onChange={() => {
														setFormReset(false);
													}}
													style={{ width: "100%" }}
													disabled={!(currentProject === "Add new project")}
												>
													{projectScopeOptions.map((option: any) => {
														return (
															<Radio.Button
																className="custom-radio-button"
																style={{
																	width:
																		projectScopeOptions.length !== 0
																			? `calc(100%/${projectScopeOptions.length})`
																			: "100%",
																	textAlign: "center",
																}}
																value={option.value}
																type="default"
															>
																{option.label}
															</Radio.Button>
														);
													})}
												</Radio.Group>
											</Form.Item>
										</div>

										<div
											style={{
												display: "flex",
												flexDirection: "row",
												flexWrap: "wrap",
												gap: "2%",
												rowGap: "20px",
											}}
										>
											<Form.Item
												label="Owner/Operator"
												name="client"
												style={{ flex: 1, flexGrow: 1 }}
												rules={[{ required: true, message: "" }]}
											>
												<Select
													getPopupContainer={(trigger: any) =>
														trigger.parentNode
													}
													filterOption={(searchedValue, options: any) => {
														return options.label
															.toLowerCase()
															.includes(searchedValue.toLowerCase());
													}}
													showSearch
													options={companies}
													onChange={(value: any) => {
														handleSelectChange("client", value);
													}}
													disabled={readOnly}
												></Select>
												{renderLookupMissing("client")}
											</Form.Item>
											<Form.Item
												label="Region"
												name="region"
												style={{ flex: 1, flexGrow: 1 }}
												rules={[{ required: true, message: "" }]}
											>
												<Select
													getPopupContainer={(trigger: any) =>
														trigger.parentNode
													}
													filterOption={(searchedValue, options: any) => {
														return options.label
															.toLowerCase()
															.includes(searchedValue.toLowerCase());
													}}
													options={regions}
													showSearch
													onChange={(value: any) => {
														handleSelectChange("region", value);
														formRef.setFieldValue("country", undefined);
														let indexArray = regions.find(
															(object: any) => object.uuid === value
														);
														if (indexArray) {
															let filteredData: any = [];
															filteredData = indexArray?.countries
																?.map((object: any) => {
																	let countries = countryList.find(
																		(element: any) =>
																			element["alpha-3"] == object
																	);
																	if (countries)
																		return {
																			label: countries.name,
																			value: countries["alpha-3"],
																		};
																})
																.filter((item: any) => item);

															setCountries(filteredData);
														}
													}}
													disabled={readOnly}
												></Select>
												{renderLookupMissing("region")}
											</Form.Item>
										</div>

										<div
											style={{
												display: "flex",
												flexDirection: "row",
												flexWrap: "wrap",
												gap: "2%",
												rowGap: "20px",
											}}
										>
											<Form.Item
												label="Country"
												name="country"
												style={{ flexGrow: 1, flex: 1 }}
												rules={[{ required: true, message: "" }]}
											>
												<Select
													getPopupContainer={(trigger: any) =>
														trigger.parentNode
													}
													filterOption={(searchedValue, options: any) => {
														return (
															options.label
																.toLowerCase()
																.includes(searchedValue.toLowerCase()) ||
															options.value
																.toLowerCase()
																.includes(searchedValue.toLowerCase())
														);
													}}
													showSearch
													options={countries}
													onChange={(value: any) => {
														handleSelectChange("country", value);
													}}
													disabled={readOnly}
												></Select>
												{renderLookupMissing("country")}
											</Form.Item>
											<Form.Item
												label="Operating office"
												name="operating_office"
												style={{ flexGrow: 1, flex: 1 }}
											>
												<Select
													getPopupContainer={(trigger: any) =>
														trigger.parentNode
													}
													filterOption={(searchedValue, options: any) => {
														return options.label
															.toLowerCase()
															.includes(searchedValue.toLowerCase());
													}}
													showSearch
													options={operatingOffices}
													onChange={(value: any) => {
														handleSelectChange("operating_office", value);
													}}
													disabled={readOnly}
												></Select>
												{renderLookupMissing("operating_office")}
											</Form.Item>
										</div>
										<Form.Item label="Project tags" name="project_tags">
											<Select
												getPopupContainer={(trigger: any) => trigger.parentNode}
												filterOption={(searchedValue, options: any) => {
													return options.label
														.toLowerCase()
														.includes(searchedValue.toLowerCase());
												}}
												showSearch
												options={projectTags}
												mode="multiple"
												tagRender={(props: any) => {
													let tag = projectTags?.find((element: any) => {
														return props.label == element.label;
													});
													if (tag) {
														return (
															<Tag
																style={{
																	whiteSpace: "normal",
																	wordWrap: "break-word",
																}}
																color={tag.color}
															>
																{tag.label}
															</Tag>
														);
													}
													return <></>;
												}}
												disabled={readOnly}
											></Select>
										</Form.Item>

										{projectScopeValue === "multi-asset" && (
											<Form.Item
												label="Project template"
												name="project_template"
											>
												<Select
													getPopupContainer={(trigger: any) =>
														trigger.parentNode
													}
													showSearch
													options={projectTemplates}
													disabled={
														currentProject !== "Add new project"
														// && !!initialProjectTemplate // cLose for now
													}
													onChange={(value: any) => {
														handleSelectChange("project_template", value);
													}}
												></Select>
											</Form.Item>
										)}

										{/* Project tools have not been implemented yet */}
										{/*  
										<Form.Item
											style={{
												gridColumn: "1/-1",
											}}
										>
											<Divider orientation="left">Project tools</Divider>
											<div>
												<span>{GetAntIcon("question3")}</span>
												<span style={{ marginLeft: "6px" }}>
													Select the modules to be included in this project
												</span>
											</div>
											<div style={{ marginTop: "4px", display: "flex" }}>
												<Switch
													onChange={() => {
														// setSwitchSelected((prev: any) => !prev);
													}}
													disabled={readOnly}
												/>
												<div
													style={{
														padding: "10px 12px",
														flexGrow: 1,
														border: "1px solid rgba(217, 217, 217, 0.3)",
														borderRadius: "2px",
													}}
												>
													<div
														style={{
															marginBottom: "4px",
															fontWeight: "bold",
														}}
													>
														Time writing
													</div>
													<div style={{ marginBottom: "8px" }}>
														Choose the level at which the user can log time for
														this project. At least one must be selected
													</div>
													<div
														style={{
															display: "flex",
															flexDirection: "column",
															gap: "8px",
														}}
													>
														<Checkbox disabled={readOnly}>Project</Checkbox>
														<Checkbox disabled={readOnly}>Phase</Checkbox>
														<Checkbox disabled={readOnly}>Task</Checkbox>
														<Checkbox disabled={readOnly}>Deliverable</Checkbox>
													</div>
												</div>
											</div>
										</Form.Item>
										*/}
									</Form>
								</div>
							</div>
							<div className="generic-footer">
								{hasDeleteProjectPermission.hasPermission && (
									<>
										{currentProject && currentProject != "Add new project" && (
											<Popconfirm
												overlayInnerStyle={{
													maxWidth: "300px",
													padding: "16px",
												}}
												placement="topLeft"
												okText="Delete"
												okType="danger"
												title=""
												icon={
													<ExclamationCircleOutlined
														style={{ color: "grey" }}
													/>
												}
												cancelText={"Cancel"}
												cancelButtonProps={{ ghost: true }}
												overlayClassName="popconfirm-danger"
												description="Permanently delete this project? This action cannot be undone."
												onOpenChange={(open: any) => {
													if (!open) {
														setEmptyModalOpen(false);
													}
												}}
												onConfirm={() => {
													setEmptyModalOpen(false);
													handleFormDelete(currentProject);
												}}
												onCancel={() => {
													setEmptyModalOpen(false);
												}}
											>
												<Button
													className="ant-btn-secondary"
													style={{ marginRight: "auto" }}
													danger
												>
													Delete
												</Button>
											</Popconfirm>
										)}
									</>
								)}

								{!readOnly && (
									<>
										<FormButtonCancel
											form={formRef}
											handleFormCancel={handleFormCancel}
											formReset={formReset}
										></FormButtonCancel>
										<FormButtonSave
											form={formRef}
											setInitialForm={setInitialForm}
											handleFormSave={handleFormSave}
											formReset={formReset}
										/>
									</>
								)}
							</div>
						</div>
					</div>
				);
			case "project-team":
				return (
					<ProjectTeam
						handleProjectConfigSave={handleProjectConfigSave}
						currentProject={currentProject}
						user={props.params?.user}
						data={allData}
						readOnly={readOnly}
					/>
				);
			case "project-outline":
				return (
					<ProjectOutline
						handleProjectConfigSave={handleProjectConfigSave}
						currentProject={currentProject}
						currentProjectData={currentProjectData}
						resetForm={resetForm}
						data={allData}
						user={props.params?.user}
						userRole={props.params?.userRole}
						handleSideContainerOpen={(condition: any) => {
							setSideContainerOpen(condition);
						}}
						navTabKey={navTabKey}
						navTabPayload={navTabPayload}
					/>
				);
			default:
				return <></>;
		}
	};

	if (
		hasEditProjectPermission.isLoading ||
		hasDeleteProjectPermission.isLoading
	) {
		return <></>;
	}

	if (isExaminerOnly) {
		return (
			<NoAccess text="Oops, looks like you don't have the authorisation to view this page." />
		);
	}

	if (currentProject !== "Add new project") {
		if (
			!hasEditProjectPermission.hasPermission &&
			!hasDeleteProjectPermission.hasPermission
		) {
			return (
				<NoAccess text="Oops, looks like you don't have the authorisation to view this page." />
			);
		}
	}

	return (
		<>
			{currentProject && !isLoading && (
				<div className="generic-container">
					<Space direction="vertical">
						<Select
							getPopupContainer={(trigger: any) => trigger.parentNode}
							className="title-selector"
							style={{
								fontSize: "20px",
								fontWeight: "600",
								color: "rgba(255, 255, 255, 0.85)",
								width: "auto",
							}}
							options={allProjects}
							value={currentProject}
							open={dropdownOpen}
							onClick={() => {
								setDropdownOpen(!dropdownOpen);
							}}
							onChange={(key: any) => {
								handleProjectChange(key, null, true);
								setDropdownOpen(false);
							}}
							onBlur={(event: any) => {
								event.preventDefault();
								console.log(event.relatedTarget);
								setDropdownOpen(false);
							}}
							dropdownStyle={{ minWidth: "100%", width: "auto" }}
							dropdownRender={(menu) => <>{menu}</>}
						></Select>

						<Tabs
							activeKey={tabKey}
							onChange={handleTabChange}
							items={TabList}
						></Tabs>
					</Space>
					<div className="project-settings-tab-content">{tabContent()}</div>

					{/* <div className="side-profile-container" style={{ flex: "1" }}></div> */}

					<Modal
						className="empty-modal"
						open={emptyModalOpen}
						closable={false}
						maskClosable={true}
						onCancel={() => {
							setEmptyModalOpen(false);
						}}
						zIndex={1050}
						footer={<></>}
						style={{ background: "transparent" }}
					>
						<></>
					</Modal>
				</div>
			)}
			{!currentProject && !isLoading && (
				<NoAccess text={"This project does not exist."} />
			)}
		</>
	);
}

Projects.propTypes = {
	params: PropTypes.any,
};

export default Projects;
